
import { reactive, inject, computed, onMounted, watch } from "vue";
import axios from "@/services/axios";
import ResearchCategorySelectionData from "@/modules/research-category-selection-data";
import ResearchInfrastructureCreationDto from "@/modules/research-infrastructure/research-infrastructure-creation-dto";
import ResearchInfrastructureRepositoryAPI from "@/modules/research-infrastructure/research-infrastructure-repository-api";
import { useI18n } from "vue-i18n";
import { location } from "@/utils/default-data";
import ResearchOrganizationRepositoryAPI from "@/modules/research-organization/research-organization-repository-api";
import {
  getPaginationValues,
  lazyParamsDefault,
} from "@/utils/pagination-helper";

export default {
  props: {
    editMode: Boolean,
  },
  setup(props: any) {
    const { t } = useI18n();
    const toast: any = inject("toast");
    const isEditModeEnabled = props.editMode as boolean;

    const state = reactive({
      filteredUsers: [],
      users: [],
      researchEquipmentId: "",
      researchEquipments: [],
      researchEquipmentItems: [] as Array<any>,
      documentUploadDialog: false,
      confirmationDialog: false,
      selectedBookingDocumentIndex: -1,
      selectedResearchInfrastructure: {},
      infrastructureDto: {
        researchEquipmentList: [],
        researchEquipmentIds: [],
        contactUsers: [],
        contactUserIds: [],
        researchGroups: [],
        researchGroupIds: [],
        researchOrganization: {},
        location: {},
        researchEquipmentData: {},
      } as ResearchInfrastructureCreationDto,
      lazyParams: lazyParamsDefault,
      dataLoading: false,
      promptRemoveEquiopment: false,
      removeEquiopmentId: "",
    });

    const { getResearchCategories, loadEquipmentCategories } =
      ResearchCategorySelectionData();

    const { researchOrganizationState } = ResearchOrganizationRepositoryAPI();

    const { getSelectedResearchInfrastructure } =
      ResearchInfrastructureRepositoryAPI();

    onMounted(() => {
      loadUsers();
      loadEquipmentCategories();
    });

    watch(
      () => getSelectedResearchInfrastructure.value,
      (newValue) => {
        if (false === isEditModeEnabled) {
          return;
        }
        state.infrastructureDto = newValue;
        state.infrastructureDto.id = newValue.id;
        state.infrastructureDto.name = newValue.name;
        state.infrastructureDto.description = newValue.description;
        state.infrastructureDto.researchOrganizationId =
          newValue.researchOrganizationId;
        state.infrastructureDto.isPublicVisible = newValue.isPublicVisible;
        state.infrastructureDto.isResearchOrganizationUseOnly =
          newValue.isResearchOrganizationUseOnly;
        state.infrastructureDto.researchEquipmentIds =
          newValue.researchEquipmentList.map((item: any) => item?.id);
        state.infrastructureDto.contactUserIds = newValue.contactUsers.map(
          (item: any) => item?.id
        );
        state.infrastructureDto.researchGroupIds = newValue.researchGroups.map(
          (item: any) => item?.id
        );

        onPaginatorResearchEquipmentClick(lazyParamsDefault);
      }
    );

    async function loadUsers() {
      const response = await axios.get("api/v1/User?PageNumber=0&PageSize=500");
      state.users = response.data;
    }

    const onPaginatorResearchEquipmentClick = (event: any): void => {
      state.lazyParams = event;
      loadEquipments();
    };

    async function loadEquipments() {
      axios
        .post(
          `api/v1/ResearchEquipment/filter?PageNumber=${state.lazyParams.page}&PageSize=${state.lazyParams.rows}`,
          {
            researchInfrastructureIds: [state.infrastructureDto.id],
            showAll: true,
          } as any
        )
        .then((response: any) => {
          state.lazyParams = getPaginationValues(response.headers);
          state.researchEquipments = response.data;
          state.dataLoading = false;
        })
        .catch(() => (state.dataLoading = false));
    }

    const onResearchEquipmentFilterChange = (event: any) => {
      const searchValue = event.value;
      if (searchValue.length < 2) {
        return;
      }

      let apiURL = `api/v1/ResearchEquipment?PageNumber=0&PageSize=25&Keyword=${searchValue}`;

      axios.get(apiURL).then((response: any) => {
        state.researchEquipmentItems = response.data;
      });
    };
    const onResearchEquipmentChange = (event: any) => {
      state.researchEquipmentId = event.value;
    };
    const onEquipmentAddClick = (): void => {
      let apiURL = `api/v1/ResearchInfrastructure/${state.infrastructureDto.id}/addEquipment/${state.researchEquipmentId}`;

      axios
        .put(apiURL)
        .then((response) => {
          if (response.status === 204) {
            const summaryText = `Oprema dodana`;
            notificationDisplay(
              "success",
              `Raziskovalna infrastruktura`,
              summaryText
            );
            onPaginatorResearchEquipmentClick(lazyParamsDefault);
          }
        })
        .catch((error) => {
          const response = error.response;
          notificationDisplay(
            "warn",
            `Raziskovalna infrastruktura`,
            `${response.statusText}: ${response.data.message}`
          );
        });
    };

    const onRemoveEquipmentClickOpenDialog = (id: any): void => {
      state.removeEquiopmentId = id;
      state.promptRemoveEquiopment = true;
    };

    const onRemoveEquipmentClick = (item: any): void => {
      let apiURL = `api/v1/ResearchInfrastructure/removeEquipment/${state.removeEquiopmentId}`;

      axios
        .delete(apiURL)
        .then((response) => {
          if (response.status === 204) {
            const summaryText = `Oprema odstranjena`;
            notificationDisplay(
              "success",
              `Raziskovalna infrastruktura`,
              summaryText
            );
            onPaginatorResearchEquipmentClick(lazyParamsDefault);
          }
        })
        .catch((error) => {
          const response = error.response;
          notificationDisplay(
            "warn",
            `Raziskovalna infrastruktura`,
            `${response.statusText}: ${response.data.message}`
          );
        });
      state.promptRemoveEquiopment = false;
    };

    const onResearchInfrastructureCreateClick = () => {
      if (isEditModeEnabled) {
        axios
          .put(
            `/api/v1/ResearchInfrastructure/${state.infrastructureDto.id}`,
            state.infrastructureDto
          )
          .then((response) => {
            if (response.status === 204) {
              const summaryText = `${state.infrastructureDto.name} posodobljena`;
              notificationDisplay(
                "success",
                `Raziskovalna infrastructura`,
                summaryText
              );
            }
          })
          .catch((error) => {
            const response = error.response;
            notificationDisplay(
              "warn",
              `Raziskovalna infrastructura`,
              `${response.statusText}: ${response.data.message}`
            );
          });
        return;
      }
      axios
        .post("/api/v1/ResearchInfrastructure", state.infrastructureDto)
        .then((response: any) => {
          if (response.status === 201) {
            state.infrastructureDto = {} as ResearchInfrastructureCreationDto;
            const summaryText = `${response.data.name} dodana`;
            notificationDisplay(
              "success",
              `Raziskovalna infrastructura`,
              summaryText
            );
          }
        })
        .catch((error) => {
          const response = error.response;
          notificationDisplay(
            "warn",
            `Raziskovalna infrastructura`,
            `${response.statusText}: ${response.data.message}`
          );
        });
    };

    watch(
      () => state.infrastructureDto.researchOrganizationId,
      (newValue) => {
        state.filteredUsers = state.users.filter(
          (item: any) => item.researchOrganizationId === newValue
        );
      }
    );

    const notificationDisplay = (
      severity: string,
      summary: string,
      detail: string
    ): void => {
      toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 3000,
      });
    };

    return {
      t,
      state: state,
      isEditModeEnabled,
      onResearchEquipmentFilterChange,
      onResearchEquipmentChange,
      onEquipmentAddClick,
      onRemoveEquipmentClick,
      onRemoveEquipmentClickOpenDialog,
      getResearchCategories: getResearchCategories,
      researchOrganizationState: researchOrganizationState,
      onResearchInfrastructureCreateClick,
      onPaginatorResearchEquipmentClick,
    };
  },
};
